import { useCallback } from 'react';
import { message } from 'antd';
import { useDispatch } from 'react-redux';
import { ViewAsUserApi } from '@/apis/auth';
import { useSpin } from '@/components/SpinContext';
import { useSessionTransition, useUnAuthorize } from '@/hooks';
import {
  setPreviewUser,
  PreviewUserMetadata,
} from '@/redux-toolkit/slice/previewSlice';

export type ViewAsUserTarget =
  string | number | (PreviewUserMetadata & { id: string | number });

export interface UseViewAsUserReturn {
  handleViewAsUser: (
    target: ViewAsUserTarget,
    isSpin?: boolean,
    showSuccessMsg?: boolean,
    preserveRoute?: boolean
  ) => Promise<boolean>;
}

/**
 * Reusable hook to handle the "View As User" / Impersonation action.
 * Wraps loading spinner, API call, error handling, session transition,
 * and updates Redux previewUser state ONLY after API success.
 */
export const useViewAsUser = (): UseViewAsUserReturn => {
  const dispatch = useDispatch();
  const { showSpin, hideSpin } = useSpin();
  const { unauthorize } = useUnAuthorize();
  const { applyAuthSession } = useSessionTransition();

  const handleViewAsUser = useCallback(
    async (
      target: ViewAsUserTarget,
      isSpin: boolean = false,
      showSuccessMsg: boolean = true,
      preserveRoute: boolean = false
    ): Promise<boolean> => {
      let targetUserId: string | number | undefined;

      if (typeof target === 'string' || typeof target === 'number') {
        targetUserId = target;
      } else if (target && typeof target === 'object' && target.id) {
        targetUserId = target.id;
      }

      if (!targetUserId) {
        return false;
      }

      if (isSpin) {
        showSpin();
      }

      try {
        const res = await ViewAsUserApi(targetUserId);
        const resData = res?.data?.data;
        const isSuccess = resData?.is_success;
        const access = resData?.data?.access;
        const refresh = resData?.data?.refresh;
        if (isSuccess && access) {
          const successMsg = resData?.message || 'Switching to user view...';

          const isRefreshed = await applyAuthSession({
            access,
            refresh,
            successMessage: showSuccessMsg === true ? successMsg : '',
            mode: 'enterPreview',
            preserveRoute,
          });
          // Update Redux state ONLY on successful session transition
          if (isRefreshed && target && typeof target === 'object') {
            dispatch(setPreviewUser(target as PreviewUserMetadata));
          }

          return !!isRefreshed;
        } else {
          const errorMsg = resData?.message || 'Failed to switch user view';
          message.error(errorMsg);
          return false;
        }
      } catch (error) {
        unauthorize(error);
        return false;
      } finally {
        hideSpin();
      }
    },
    [showSpin, hideSpin, unauthorize, applyAuthSession, dispatch]
  );

  return {
    handleViewAsUser,
  };
};

export default useViewAsUser;
